import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Column, DataTable, DataTableContainer, TableActionButton } from 'components/DataTable';
import { useClubSlug } from 'components/Clubs/hooks';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MatchCandidate } from './types';

interface Props {
    onSelect: (candidate: MatchCandidate) => void;
}

/** One member row as delivered by the club-member list endpoint. */
interface ClubMemberRow {
    id: number;
    name: string;
    number?: string | null;
    email?: string | null;
}

/**
 * Member picker shown in a modal: the full member management as a searchable, paginated table,
 * reduced to a single "pick this one" action so no destructive action is reachable from here.
 */
const ClubMemberPickerModal = ({ onSelect }: Props) => {
    // Provides the translated column headers and the select tooltip.
    const { t } = useTranslation('bookings');
    // Scopes the member list request to the current club.
    const clubSlug = useClubSlug();

    // Columns of the picker: just enough to tell two similar members apart.
    const columns: Column[] = useMemo(
        () => [
            { name: t('booking.createMember.picker.columns.name'), uid: 'name' },
            { name: t('booking.createMember.picker.columns.number'), uid: 'number' },
            { name: t('booking.createMember.picker.columns.email'), uid: 'email' }
        ],
        [t]
    );

    return (
        // Capped height with its own scroll area, so the picker stays compact instead of
        // filling the modal shell's forced ~95vh.
        <DataTableContainer className="tw-max-h-[60vh] tw-overflow-y-auto">
            <DataTable
                className="tw-flex-1"
                columns={columns}
                fetchUrl={`club-member/${clubSlug}`}
                fetchMethod="GET"
                hideSelectionAssistant
                tableProps={{
                    sticked: true,
                    bordered: true,
                    borderWeight: 'light',
                    compact: false,
                    containerCss: {}
                }}
                rowActions={(item: ClubMemberRow) => (
                    <TableActionButton.Container justify="flex-end">
                        <TableActionButton
                            icon={faCheck}
                            tooltip={t('booking.createMember.picker.select')}
                            onPress={() =>
                                onSelect({
                                    id: item.id,
                                    name: item.name,
                                    number: item.number,
                                    score: 0,
                                    reasons: []
                                })
                            }
                        />
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default ClubMemberPickerModal;
